import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { videos } from "@/mocks/videos";
import VideoCard from "@/components/feature/VideoCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import usePageMeta from "@/hooks/usePageMeta";
import CategoryFilters, { type SortKey } from "./components/CategoryFilters";

const PAGE_SIZE = 8;

export default function CategoryPage() {
  const { categoryId } = useParams();
  const category = categories.find((c) => c.id === categoryId);

  const [sort, setSort] = useState<SortKey>("latest");
  const [activeTag, setActiveTag] = useState("");
  const [visible, setVisible] = useState(PAGE_SIZE);

  useEffect(() => {
    setSort("latest");
    setActiveTag("");
    setVisible(PAGE_SIZE);
  }, [categoryId]);

  const categoryVideos = useMemo(
    () => videos.filter((v) => v.categoryId === categoryId),
    [categoryId],
  );

  const tags = useMemo(() => {
    const set = new Set<string>();
    categoryVideos.forEach((v) => v.tags.forEach((t) => set.add(t)));
    return Array.from(set);
  }, [categoryVideos]);

  const filtered = useMemo(() => {
    const list = categoryVideos.filter((v) => (activeTag ? v.tags.includes(activeTag) : true));
    const sorted = [...list];
    if (sort === "latest") sorted.sort((a, b) => b.publishTime.localeCompare(a.publishTime));
    if (sort === "oldest") sorted.sort((a, b) => a.publishTime.localeCompare(b.publishTime));
    if (sort === "hot") sorted.sort((a, b) => b.views - a.views);
    return sorted;
  }, [categoryVideos, activeTag, sort]);

  usePageMeta({
    title: category ? `${category.name}视频 - 吃瓜视频` : "视频分类 - 吃瓜视频",
    description: category
      ? `${category.description} 吃瓜视频${category.name}频道持续更新最新相关内容。`
      : "吃瓜视频视频分类，按兴趣浏览明星动态、社会热点与趣味短片。",
    keywords: category
      ? `${category.name},吃瓜视频,${category.name}视频,娱乐视频`
      : "吃瓜视频,视频分类,娱乐视频",
  });

  if (!category) {
    return (
      <div className="w-full px-4 py-20 md:px-8">
        <div className="mx-auto flex max-w-md flex-col items-center rounded-lg border border-background-200 bg-background-50 px-6 py-14 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-3xl text-primary-500">
            <i className="ri-emotion-sad-line" />
          </span>
          <h1 className="mt-4 font-heading text-lg font-bold text-foreground-950">没有找到这个分类</h1>
          <p className="mt-2 text-sm text-foreground-600">该分类可能已调整，去看看其他内容吧。</p>
          <Link
            to="/"
            className="mt-5 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const shown = filtered.slice(0, visible);

  return (
    <div className="w-full px-4 py-6 md:px-8 md:py-8">
      <Breadcrumb
        items={[
          { label: "首页", href: "/" },
          { label: "视频分类", href: "/" },
          { label: category.name },
        ]}
      />

      <div className="mt-5 flex items-start gap-4">
        <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-3xl text-primary-600">
          <i className={category.icon} />
        </span>
        <div>
          <h1 className="font-heading text-2xl font-black tracking-tight text-foreground-950 md:text-3xl">
            {category.name}
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-foreground-600">{category.description}</p>
        </div>
      </div>

      <div className="mt-6">
        <CategoryFilters
          sort={sort}
          onSortChange={(s) => {
            setSort(s);
            setVisible(PAGE_SIZE);
          }}
          tags={tags}
          activeTag={activeTag}
          onTagChange={(t) => {
            setActiveTag(t);
            setVisible(PAGE_SIZE);
          }}
          resultCount={filtered.length}
        />
      </div>

      {filtered.length === 0 ? (
        <div className="mt-10 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-3xl text-secondary-600">
            <i className="ri-filter-off-line" />
          </span>
          <h2 className="mt-4 font-heading text-base font-bold text-foreground-950">暂无匹配的视频</h2>
          <p className="mt-2 text-sm text-foreground-600">换个筛选条件试试，或重置筛选查看全部内容。</p>
          <button
            type="button"
            onClick={() => {
              setActiveTag("");
              setSort("latest");
            }}
            className="mt-5 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
          >
            重置筛选
          </button>
        </div>
      ) : (
        <>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 md:gap-5">
            {shown.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>

          {filtered.length > shown.length && (
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                onClick={() => setVisible((v) => v + PAGE_SIZE)}
                className="flex items-center gap-2 rounded-full border border-background-300 bg-background-50 px-6 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600 whitespace-nowrap"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-arrow-down-line text-base" />
                </span>
                加载更多（{filtered.length - shown.length}）
              </button>
            </div>
          )}

          {filtered.length <= shown.length && (
            <p className="mt-8 text-center text-xs text-foreground-500">已经到底啦，去看看其他分类吧</p>
          )}
        </>
      )}
    </div>
  );
}